import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import type {
  SupportTicket,
  TicketAttachment,
  TicketHistoryEntry,
  TicketHistoryResponse,
  TicketReply,
} from "@/lib/support";
import { replyAttachments, ticketLevelAttachments } from "@/lib/support";
import { SupportAttachments } from "@/pages/support/SupportAttachments";
import { TicketBrief } from "@/pages/support/TicketBrief";
import {
  selectCurrentSubject,
  statusNoteDuplicatesReply,
} from "@/pages/support/ticketConversation";
import {
  confirmTicketResolved,
  fetchTicketHistory,
  markSupportTicketResolved,
  postTicketReply,
  reopenSupportTicket,
  updateTicketMessage,
} from "@/lib/support";
import {
  STATUS_LABEL,
  STATUS_USER_HINT,
  URGENCY_LABEL,
  URGENCY_USER_HINT,
} from "@/pages/support/constants";
import { StatusBadge, UrgencyBadge } from "@/pages/support/SupportBadges";
import { fmtDate, fmtRelative, toastErr, toastOk } from "@/pages/support/utils";
import { getUserLocalLogin, useCurrentUser } from "@/lib/auth";

export function UserDetailPanel({
  ticket,
  onRefresh,
  onBack,
  showBack,
}: {
  ticket: SupportTicket;
  onRefresh: () => void;
  onBack?: () => void;
  showBack?: boolean;
}) {
  const { user } = useCurrentUser();
  const login = getUserLocalLogin(user).toLowerCase();
  const [hist, setHist] = useState<TicketHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenMessage, setReopenMessage] = useState("");
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState("");
  const ticketRef = useRef(ticket);
  ticketRef.current = ticket;

  const load = useCallback(async () => {
    const listTicket = ticketRef.current;
    setLoading(true);
    try {
      const data = await fetchTicketHistory(listTicket.id);
      setHist(data);
    } catch (err) {
      console.error(err);
      setHist(null);
    } finally {
      setLoading(false);
    }
  }, [ticket.id]);

  useEffect(() => {
    void load();
    setReply("");
  }, [ticket.id, load]);

  const merged = useMemo(() => {
    const raw = (hist?.ticket || {}) as SupportTicket;
    return {
      ...ticket,
      ...raw,
      message: raw.message ?? ticket.message ?? null,
      created_at: raw.created_at ?? ticket.created_at,
      updated_at: raw.updated_at ?? ticket.updated_at,
    };
  }, [ticket, hist]);

  const status = String(merged.status || "open").toLowerCase();
  const urgency = String(merged.urgency || "normal").toLowerCase();
  const isClosed = status === "closed";
  const canReply = !isClosed;
  const canEdit =
    status === "open" && String(merged.reporter_login || "").toLowerCase() === login;
  const canConfirm = status === "resolved";
  const canReopen = status === "resolved" || status === "closed";
  const canMarkResolved = status === "open" || status === "in_progress" || status === "waiting";
  const waitingOnReporter = status === "waiting" && merged.action_required !== false;

  const timeline = useMemo(() => {
    const items: {
      at: string;
      kind: "status" | "reply" | "description";
      entry: TicketHistoryEntry | TicketReply | null;
      label?: string;
      body?: string;
    }[] = [];

    if (merged.message) {
      items.push({
        at: merged.created_at || "",
        kind: "description",
        entry: null,
        label: merged.reporter_display || "Du",
        body: merged.message,
      });
    }
    for (const h of hist?.history || []) {
      if (h.changed_at) items.push({ at: h.changed_at, kind: "status", entry: h });
    }
    for (const r of hist?.replies || []) {
      if (r.created_at) items.push({ at: r.created_at, kind: "reply", entry: r });
    }
    return items.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  }, [hist, merged]);

  const currentSubject = useMemo(
    () =>
      selectCurrentSubject({
        original: merged.message,
        reporterLogin: merged.reporter_login,
        history: hist?.history,
        replies: hist?.replies,
      }),
    [hist, merged.message, merged.reporter_login],
  );

  async function handleSendReply() {
    const msg = reply.trim();
    if (!msg) return toastErr("Bitte gib eine Nachricht ein");
    setSending(true);
    try {
      await postTicketReply(ticket.id, msg);
      setReply("");
      toastOk("Nachricht gesendet");
      await load();
      onRefresh();
    } catch (err) {
      console.error(err);
      toastErr("Nachricht konnte nicht gesendet werden");
    } finally {
      setSending(false);
    }
  }

  async function handleConfirmResolved(note = "") {
    setActionLoading(true);
    try {
      await confirmTicketResolved(ticket.id, note.trim());
      toastOk("Danke – Ticket wurde abgeschlossen");
      await load();
      onRefresh();
    } catch (err) {
      console.error(err);
      toastErr("Bestätigung fehlgeschlagen");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleMarkResolved() {
    const note = resolveNote.trim();
    setActionLoading(true);
    try {
      await markSupportTicketResolved(ticket.id, note);
      toastOk("Ticket als gelöst markiert");
      setResolveOpen(false);
      setResolveNote("");
      await load();
      onRefresh();
    } catch (err) {
      console.error(err);
      toastErr("Ticket konnte nicht als gelöst markiert werden");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReopen() {
    const msg = reopenMessage.trim();
    setActionLoading(true);
    try {
      await reopenSupportTicket(ticket.id, msg);
      toastOk("Ticket wieder geöffnet");
      setReopenOpen(false);
      setReopenMessage("");
      await load();
      onRefresh();
    } catch (err) {
      console.error(err);
      toastErr("Ticket konnte nicht wieder geöffnet werden");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    const msg = editMessage.trim();
    if (msg.length < 10) return toastErr("Beschreibung muss mindestens 10 Zeichen haben");
    setActionLoading(true);
    try {
      await updateTicketMessage(ticket.id, msg);
      toastOk("Beschreibung aktualisiert");
      setEditOpen(false);
      await load();
      onRefresh();
    } catch (err) {
      console.error(err);
      toastErr("Beschreibung konnte nicht gespeichert werden");
    } finally {
      setActionLoading(false);
    }
  }

  if (loading && !hist) {
    return (
      <div className="flex h-full min-h-[20rem] items-center justify-center rounded-ios-lg border border-separator bg-card">
        <p className="text-sm text-label-secondary">Ticket wird geladen …</p>
      </div>
    );
  }

  return (
    <>
      <article className="support-user-detail flex h-full min-h-0 flex-col overflow-hidden rounded-ios-lg border border-separator bg-card shadow-ios">
        <header className="shrink-0 border-b border-separator px-4 py-4">
          {showBack && onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="mb-2 text-sm font-medium text-accent hover:underline lg:hidden"
            >
              ← Zurück zur Liste
            </button>
          ) : null}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-mono text-xs text-label-secondary">Ticket #{merged.id}</p>
              <h2 className="mt-0.5 text-lg font-semibold text-label">{merged.title}</h2>
              <div className="mt-2 flex flex-wrap gap-2">
                <StatusBadge status={status} label={STATUS_LABEL[status] || status} />
                <UrgencyBadge urgency={urgency} label={URGENCY_LABEL[urgency] || urgency} />
              </div>
            </div>
            {canEdit ? (
              <Button
                type="button"
                variant="secondary"
                className="text-xs"
                onClick={() => {
                  setEditMessage(String(merged.message || ""));
                  setEditOpen(true);
                }}
              >
                Beschreibung bearbeiten
              </Button>
            ) : null}
          </div>

          <div
            className={clsx(
              "mt-3 rounded-ios border px-3 py-2.5 text-sm",
              waitingOnReporter && "border-status-orange/40 bg-status-orange/10 text-label",
              status === "resolved" && "border-status-green/40 bg-status-green/10 text-label",
              !waitingOnReporter && status !== "resolved" && "border-separator/60 bg-fill-quaternary/20 text-label-secondary",
            )}
          >
            {waitingOnReporter
              ? STATUS_USER_HINT.waiting
              : status === "waiting"
                ? "Deine Antwort ist eingegangen. Ich schaue sie mir an."
                : STATUS_USER_HINT[status] || "Status wird aktualisiert."}
          </div>

          {(canMarkResolved || canConfirm || canReopen) && !isClosed ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {canMarkResolved ? (
                <Button
                  type="button"
                  className="text-xs bg-status-green text-white hover:opacity-90"
                  disabled={actionLoading}
                  onClick={() => setResolveOpen(true)}
                >
                  Als gelöst markieren
                </Button>
              ) : null}
              {canConfirm ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="text-xs"
                  disabled={actionLoading}
                  onClick={() => void handleConfirmResolved()}
                >
                  Lösung bestätigen & schließen
                </Button>
              ) : null}
              {canReopen ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="text-xs"
                  disabled={actionLoading}
                  onClick={() => setReopenOpen(true)}
                >
                  Problem besteht noch – Wieder öffnen
                </Button>
              ) : null}
            </div>
          ) : null}
        </header>

        <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-[minmax(0,1fr)_220px]">
          <div className="min-h-0 flex flex-col border-b border-separator lg:border-b-0 lg:border-r">
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              {currentSubject ? (
                <TicketBrief
                  className="mb-5"
                  original={merged.message || ""}
                  originalAt={merged.created_at}
                  reporterLabel={merged.reporter_display || "Du"}
                  current={currentSubject}
                  showOriginal={false}
                />
              ) : null}
              <h3 className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Verlauf</h3>
              <div className="mt-3 space-y-3">
                {timeline.length === 0 ? (
                  <p className="text-sm text-label-tertiary">Noch keine Aktivität.</p>
                ) : (
                  timeline.map((item, i) => {
                    const statusEntry = item.kind === "status" ? (item.entry as TicketHistoryEntry) : null;
                    return (
                      <TimelineItem
                        key={`${item.kind}-${i}`}
                        item={item}
                        currentLogin={login}
                        attachments={hist?.attachments}
                        hideStatusNote={
                          statusEntry
                            ? statusNoteDuplicatesReply(statusEntry.note, statusEntry.changed_at, hist?.replies)
                            : false
                        }
                      />
                    );
                  })
                )}
              </div>

              {ticketLevelAttachments(hist?.attachments).length ? (
                <SupportAttachments
                  attachments={ticketLevelAttachments(hist?.attachments)}
                  variant="grid"
                  title="Anhänge"
                  className="mt-6"
                />
              ) : null}
            </div>

            {canReply ? (
              <div className="shrink-0 border-t border-separator bg-fill-quaternary/10 px-4 py-3">
                <label className="eh-label text-xs">Deine Nachricht</label>
                <textarea
                  className="eh-input mt-1 min-h-[5rem] text-sm"
                  placeholder="Deine Rückmeldung oder Nachfrage …"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={3}
                />
                <div className="mt-2 flex justify-end">
                  <Button type="button" disabled={sending || actionLoading} onClick={() => void handleSendReply()}>
                    {sending ? "Wird gesendet …" : "Nachricht senden"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="shrink-0 border-t border-separator bg-fill-quaternary/10 px-4 py-3 text-sm text-label-secondary">
                Dieses Ticket ist geschlossen. Bei einem neuen Problem erstelle einfach ein neues Ticket.
              </div>
            )}
          </div>

          <aside className="shrink-0 px-4 py-4 text-sm">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Details</h3>
            <dl className="mt-3 space-y-3">
              <MetaRow label="Programm" value={merged.program || "—"} />
              <MetaRow label="Dringlichkeit" value={URGENCY_LABEL[urgency] || urgency} hint={URGENCY_USER_HINT[urgency]} />
              <MetaRow label="Erstellt" value={fmtDate(merged.created_at)} />
              <MetaRow label="Aktualisiert" value={fmtRelative(merged.updated_at || merged.created_at)} />
              <MetaRow label="Status" value={STATUS_LABEL[status] || status} />
            </dl>
          </aside>
        </div>
      </article>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Beschreibung bearbeiten"
        size="lg"
        dirty={editMessage !== String(merged.message || "")}
      >
        <form onSubmit={handleSaveEdit} className="space-y-4">
          <p className="text-sm text-label-secondary">
            Solange das Ticket offen ist, kannst du die ursprüngliche Beschreibung anpassen.
          </p>
          <textarea
            className="eh-input min-h-[10rem]"
            value={editMessage}
            required
            minLength={10}
            maxLength={5000}
            onChange={(e) => setEditMessage(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setEditOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={actionLoading}>
              Speichern
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={resolveOpen}
        onClose={() => setResolveOpen(false)}
        title="Als gelöst markieren"
        size="md"
        dirty={!!resolveNote}
      >
        <div className="space-y-4">
          <p className="text-sm text-label-secondary">
            Teile mir optional mit, wie das Problem gelöst wurde. Ich werde informiert.
          </p>
          <textarea
            className="eh-input min-h-[5rem]"
            placeholder="Optional: Kurze Anmerkung zur Lösung …"
            value={resolveNote}
            maxLength={5000}
            onChange={(e) => setResolveNote(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setResolveOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              className="bg-status-green text-white hover:opacity-90"
              disabled={actionLoading}
              onClick={() => void handleMarkResolved()}
            >
              {actionLoading ? "Wird gespeichert …" : "Als gelöst markieren"}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
        title="Ticket wieder öffnen"
        size="md"
        dirty={!!reopenMessage}
      >
        <div className="space-y-4">
          <p className="text-sm text-label-secondary">
            Beschreibe kurz, warum das Problem noch besteht. Ich kümmere mich darum.
          </p>
          <textarea
            className="eh-input min-h-[6rem]"
            placeholder="Das Problem tritt weiterhin auf, weil …"
            value={reopenMessage}
            maxLength={5000}
            onChange={(e) => setReopenMessage(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setReopenOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" disabled={actionLoading} onClick={() => void handleReopen()}>
              {actionLoading ? "Wird geöffnet …" : "Ticket wieder öffnen"}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

function MetaRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <dt className="text-xs text-label-tertiary">{label}</dt>
      <dd className="mt-0.5 font-medium text-label">{value}</dd>
      {hint ? <dd className="mt-0.5 text-xs text-label-secondary">{hint}</dd> : null}
    </div>
  );
}

function TimelineItem({
  item,
  currentLogin,
  attachments,
  hideStatusNote = false,
}: {
  item: {
    at: string;
    kind: "status" | "reply" | "description";
    entry: TicketHistoryEntry | TicketReply | null;
    label?: string;
    body?: string;
  };
  currentLogin: string;
  attachments?: TicketAttachment[] | null;
  hideStatusNote?: boolean;
}) {
  if (item.kind === "description") {
    return (
      <div className="rounded-ios border border-separator/60 bg-fill-quaternary/15 px-3 py-2.5">
        <div className="flex justify-between gap-2 text-xs text-label-tertiary">
          <span>{item.label || "Du"} · Ursprüngliche Anfrage</span>
          <span title={fmtDate(item.at)}>{fmtRelative(item.at)}</span>
        </div>
        <p className="mt-1.5 whitespace-pre-wrap text-sm text-label">{item.body}</p>
      </div>
    );
  }

  if (item.kind === "status") {
    const h = item.entry as TicketHistoryEntry;
    return (
      <div className="flex gap-3 px-1 py-1">
        <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-label-tertiary" aria-hidden />
        <div className="min-w-0 flex-1 text-sm">
          <div className="flex flex-wrap justify-between gap-1 text-xs text-label-tertiary">
            <span>{h.changed_by_display || h.changed_by_login || "Support"} · Status</span>
            <span title={fmtDate(h.changed_at)}>{fmtRelative(h.changed_at)}</span>
          </div>
          <p className="mt-0.5 text-label">
            {STATUS_LABEL[h.old_status || ""] || h.old_status || "—"} →{" "}
            <strong>{STATUS_LABEL[h.new_status || ""] || h.new_status}</strong>
          </p>
          {h.note && !hideStatusNote ? (
            <p className="mt-1 whitespace-pre-wrap text-label-secondary">{h.note}</p>
          ) : null}
        </div>
      </div>
    );
  }

  const r = item.entry as TicketReply;
  const authorLogin = String(r.author_login || "").toLowerCase();
  const isMine = authorLogin === currentLogin;
  return (
    <div
      className={clsx(
        "rounded-ios border px-3 py-2.5 text-sm",
        isMine ? "border-accent/30 bg-accent-soft/20 ml-4" : "border-separator bg-card mr-4",
      )}
    >
      <div className="flex justify-between gap-2 text-xs text-label-tertiary">
        <span>{isMine ? "Du" : r.author_display || r.author_login || "Support"}</span>
        <span title={fmtDate(r.created_at)}>{fmtRelative(r.created_at)}</span>
      </div>
      {r.message?.trim() ? <p className="mt-1.5 whitespace-pre-wrap text-label">{r.message}</p> : null}
      {replyAttachments(attachments, r.id).length ? (
        <SupportAttachments
          attachments={replyAttachments(attachments, r.id)}
          variant="grid"
          showTitle={false}
          className="mt-2"
        />
      ) : null}
    </div>
  );
}
